import { apiBase } from './apiBase';
import { ApiError, apiFetch } from './api';
import { useAuthStore } from '../store/useAuthStore';

// Client for the onboarding history imports (facturi 04, reconciliere 07, …).
// The workbook is the raw body of both calls; the confirm re-sends the same
// file with the preview's hash, and the server applies it only if the result
// still matches what the user saw (409 PREVIEW_CHANGED otherwise).

export type ImportRowStatus = 'valid' | 'warning' | 'error';

export interface ProjectSuggestion {
  id: string;
  name: string;
  code: string | null;
}

/** How the file's project codes resolve (same block for 04 and 07). */
export interface ImportProjectsPreview {
  newProjects: {
    code: string;
    name: string;
    clientName: string | null;
    clientFound: boolean;
    suggestions: ProjectSuggestion[];
  }[];
  mappedProjects: {
    code: string;
    projectId: string;
    projectName: string;
    suggestions: ProjectSuggestion[];
  }[];
  unknownCodes: { code: string; suggestions: ProjectSuggestion[] }[];
  clientLinks: { projectName: string; clientName: string }[];
}

export type InvoiceRowAction = 'create' | 'update' | 'unchanged' | 'skip';

export interface InvoiceImportRow {
  rowNumber: number;
  status: ImportRowStatus;
  issues: string[];
  action: InvoiceRowAction;
  data: {
    direction: 'INCOME' | 'OUTCOME';
    invoiceNumber: string;
    variant: number;
    issueDate: string | null;
    dueDate: string | null;
    partnerName: string;
    partnerCif: string | null;
    partnerType: 'client' | 'supplier' | 'person' | null;
    total: number;
    currency: string;
    invoiceStatus: 'DRAFT' | 'SENT' | 'PAID' | 'OVERDUE' | 'CANCELLED';
    paidAt: string | null;
    projectCode: string | null;
  };
}

export interface InvoiceImportPreview {
  fileHash: string;
  previewHash: string;
  rows: InvoiceImportRow[];
  summary: {
    total: number;
    create: number;
    update: number;
    unchanged: number;
    skip: number;
    errors: number;
    warnings: number;
  };
  projects: ImportProjectsPreview;
}

export interface InvoiceImportResult {
  batchId: string;
  created: number;
  updated: number;
  unchanged: number;
  skipped: number;
  failed: { rowNumber: number; error: string }[];
  projectsCreated: number;
  projectClientsLinked: number;
}

export interface OnboardingImportParams {
  companyId?: string | null;
  fileName?: string | null;
  previewHash?: string;
  /** File code → existing project id, chosen in the preview. */
  projectMap?: Record<string, string>;
  [extra: string]: unknown;
}

function query(params: OnboardingImportParams): string {
  const q = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    q.set(k, typeof v === 'string' ? v : JSON.stringify(v));
  }
  const s = q.toString();
  return s ? `?${s}` : '';
}

async function postWorkbook<T>(
  path: string,
  file: File,
  params: OnboardingImportParams,
): Promise<T> {
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(`${apiBase()}${path}${query(params)}`, {
    method: 'POST',
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });
  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : undefined;
  } catch {
    parsed = undefined;
  }
  if (!res.ok) {
    const err =
      parsed && typeof parsed === 'object' && 'error' in parsed
        ? ((parsed as { error: { message?: string; code?: string } }).error ?? {})
        : {};
    throw new ApiError(res.status, err.message ?? 'Importul a eșuat.', err.code);
  }
  return (parsed as { data: T }).data;
}

export function previewInvoiceImport(file: File, params: OnboardingImportParams) {
  return postWorkbook<InvoiceImportPreview>('/api/invoices/import/excel', file, {
    ...params,
    fileName: file.name,
  });
}

export function confirmInvoiceImport(file: File, params: OnboardingImportParams) {
  return postWorkbook<InvoiceImportResult>('/api/invoices/import/confirm', file, {
    ...params,
    fileName: file.name,
  });
}

// --- Reconciliation import (07_reconciliere_import.xlsx) --------------------

export interface ReconImportRow {
  rowNumber: number;
  status: ImportRowStatus;
  issues: string[];
  action: 'apply' | 'unchanged' | 'skip';
  matchedBy: 'fingerprint' | 'fallback' | 'amount' | 'chosen' | null;
  lineId: string | null;
  candidates?: { lineId: string; statement: string; description: string | null }[];
  data: {
    date: string | null;
    type: 'credit' | 'debit' | null;
    amount: number | null;
    counterparty: string;
    importStatus: 'MANUAL_MATCHED' | 'IGNORED' | 'UNMATCHED';
    categoryCode: string | null;
    projectCode: string | null;
    allocations: { invoiceId: string; invoiceNumber: string; amount: number }[];
  };
}

export interface ReconImportPreview {
  fileHash: string;
  previewHash: string;
  format: { format: string; version: number; parser: string | null; firm: string | null };
  rows: ReconImportRow[];
  report: {
    fileRows: number;
    byFingerprint: number;
    byFallback: number;
    byAmountOnly: number;
    chosen: number;
    ambiguous: number;
    withoutLine: number;
    linesWithoutRow: number;
    linesWithoutRowSample: {
      lineId: string;
      date: string;
      amount: number;
      description: string | null;
    }[];
    apply: number;
    unchanged: number;
    skipped: number;
    errors: number;
    allocations: number;
  };
  unknownCategories: { code: string; rows: number[] }[];
  projects: ImportProjectsPreview;
}

export interface ReconImportResult {
  batchId: string;
  applied: number;
  unchanged: number;
  skipped: number;
  errors: number;
  statementsTouched: number;
  projectsCreated: number;
}

export function previewReconciliationImport(file: File, params: OnboardingImportParams) {
  return postWorkbook<ReconImportPreview>(
    '/api/bank-statements/import-reconciliation/excel',
    file,
    {
      ...params,
      fileName: file.name,
    },
  );
}

export function confirmReconciliationImport(file: File, params: OnboardingImportParams) {
  return postWorkbook<ReconImportResult>(
    '/api/bank-statements/import-reconciliation/confirm',
    file,
    {
      ...params,
      fileName: file.name,
    },
  );
}

// --- Team import (06_echipa_subcontractori.xlsx) ----------------------------

export interface TeamPersonRow {
  rowNumber: number;
  status: ImportRowStatus;
  issues: string[];
  action: 'create' | 'update' | 'unchanged' | 'skip';
  data: {
    label: string;
    fullName: string;
    email: string | null;
    type: 'CONTRACTOR' | 'EMPLOYEE' | null;
    companyName: string | null;
    companyCui: string | null;
    iban: string | null;
    existingPersonId: string | null;
  };
}

export interface TeamPeriodRow {
  rowNumber: number;
  status: ImportRowStatus;
  issues: string[];
  action: 'create' | 'update' | 'unchanged' | 'skip';
  data: {
    label: string;
    projectCode: string;
    from: string | null;
    to: string | null;
    share: number | null;
    personRow: number | null;
    existingAssignmentId: string | null;
  };
}

export interface TeamImportPreview {
  fileHash: string;
  previewHash: string;
  people: TeamPersonRow[];
  periods: TeamPeriodRow[];
  summary: {
    people: number;
    create: number;
    update: number;
    unchanged: number;
    periods: number;
    periodsNew: number;
    errors: number;
    warnings: number;
  };
  projects: ImportProjectsPreview;
}

export interface TeamImportResult {
  batchId: string;
  peopleCreated: number;
  peopleUpdated: number;
  peopleUnchanged: number;
  periodsCreated: number;
  periodsUpdated: number;
  skipped: number;
  failed: { rowNumber: number; error: string }[];
  projectsCreated: number;
}

export function previewTeamImport(file: File, params: OnboardingImportParams) {
  return postWorkbook<TeamImportPreview>('/api/onboarding/team/import/excel', file, {
    ...params,
    fileName: file.name,
  });
}

export function confirmTeamImport(file: File, params: OnboardingImportParams) {
  return postWorkbook<TeamImportResult>('/api/onboarding/team/import/confirm', file, {
    ...params,
    fileName: file.name,
  });
}

// --- Onboarding steps per firm + import history ------------------------------

export type OnboardingStepKey =
  | 'mode'
  | 'categorii'
  | 'conturi'
  | 'echipa'
  | 'clienti'
  | 'furnizori'
  | 'abonamente'
  | 'facturi'
  | 'extrase'
  | 'reconciliere';
export type OnboardingStepStatus = 'pending' | 'done' | 'skipped' | 'error';

export interface ImportBatchSummary {
  id: string;
  kind: string;
  fileName: string | null;
  stats: Record<string, number> | null;
  createdAt: string;
}

export interface OnboardingStepState {
  step: OnboardingStepKey;
  status: OnboardingStepStatus;
  updatedAt: string;
  importBatch: ImportBatchSummary | null;
}

const authHeaders = (): Record<string, string> => {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export async function getOnboardingSteps(
  companyId: string,
): Promise<{ companyId: string; steps: OnboardingStepState[] }> {
  const res = await apiFetch<{ data: { companyId: string; steps: OnboardingStepState[] } }>(
    `/api/onboarding/steps?companyId=${encodeURIComponent(companyId)}`,
    { headers: authHeaders() },
  );
  return res.data;
}

export async function setOnboardingStep(
  step: OnboardingStepKey,
  body: { companyId: string; status: OnboardingStepStatus; importBatchId?: string | null },
): Promise<void> {
  await apiFetch(`/api/onboarding/steps/${step}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(body),
  });
}

export interface ImportBatchRow {
  id: string;
  kind: string;
  fileName: string | null;
  format: string | null;
  formatVersion: number | null;
  companyId: string | null;
  companyName: string | null;
  createdBy: string | null;
  createdAt: string;
  stats: Record<string, number> | null;
}

/** Import history of a firm (Setări → Onboarding → Istoric importuri). */
export async function listImportBatches(companyId: string): Promise<ImportBatchRow[]> {
  const res = await apiFetch<{ data: ImportBatchRow[] }>(
    `/api/import-batches?companyId=${encodeURIComponent(companyId)}`,
    { headers: authHeaders() },
  );
  return res.data;
}
